import React, { useState } from "react"
import { useTranslation } from "react-i18next"
import { FileUpload } from "../../models/file"
import { User, UpdateUserRequest } from "../../models/user"
import { Form, Row, Col, Container, Button } from "react-bootstrap"
import { Formik, FormikProps } from "formik"
import { preventSubmitOnEnter } from "../../utility/common/preventSubmitOnEnter"
import { nameof } from "../../utility/common/nameof"
import "./UserProfileForm.scss"
import HorizontalValidatableInput from "../HorizontalValidatableInput/HorizontalValidatableInput"
import PhoneInput from "../PhoneInput/PhoneInput"
import PasswordInput from "../PasswordInput/PasswordInput"
import { locales } from "../../locales"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCamera } from "@fortawesome/pro-light-svg-icons/faCamera"
import UserAvatar from "../UserAvatar/UserAvatar"
import ImageUploadModal from "../ImageUploadModal/ImageUploadModal"
import defaultAvatar from "../../assets/images/defaultUserAvatar.png"
import { useAppDispatch, useAppSelector } from "../../store/hooks"
import { updateUser, updateAvatar } from "../../store/users/thunks"
import { formTranslation } from "../../locales/form"
import { selectUpdateAvatarState, selectUpdateUserState } from "../../store/users/selectors"
import LoadingButton from "../LoadingButton/LoadingButton"
import AuthenticationTokenInput from "../AuthenticationTokenInput/AuthenticationTokenInput"
import RadioButtonValidatableInput from "../RadioButtonValidatableInput/RadioButtonValidatableInput"
import useRefCheck from "../../hooks/useRefCheck"
import { testId } from "../../utility/tests/testId/users"
import { ProfileValues, SendKeyCombinations } from "../../models/profile"
import { getValidationSchema } from "./schema/UserProfileForm.schema"
import { MESSAGE_SEND_BEHAVIOUR_ENABLED } from "../../utility/common/flags"
import { useProjectSettingCheck } from "../../hooks/useProjectSettingCheck"
import { NewWorkplaceOperatorProjectSettings } from "../../models/projectSettings"

export const tNamespace = "user:"

interface Props {
    user: User
}

const renderKeyCombinationLabel = (
    firstKey: string,
    secondKey: string,
    firstKeyLabel: string,
    secondKeyLabel: string
) => {
    return (
        <div>
            <span>
                <b>{firstKey}</b> {firstKeyLabel}
            </span>
            <br />
            <span>
                <b>{secondKey}</b> {secondKeyLabel}
            </span>
        </div>
    )
}

const FormikUserProfileForm: React.FC<Props & FormikProps<ProfileValues>> = props => {
    const { user, handleSubmit, dirty: isFormDirty } = props
    const { t } = useTranslation()
    const dispatch = useAppDispatch()

    const [show, setShow] = useState(false)
    const updateUserState = useAppSelector(selectUpdateUserState)
    const updateAvatarState = useAppSelector(selectUpdateAvatarState)
    const isMessageSendBehaviourSettingEnabled = useRefCheck(MESSAGE_SEND_BEHAVIOUR_ENABLED)

    const languagesToTranslateSetting = useProjectSettingCheck<
        NewWorkplaceOperatorProjectSettings,
        "TranslateLanguages"
    >("TranslateLanguages")
    const languagesToTranslate = languagesToTranslateSetting ? languagesToTranslateSetting.Values : []
    const isAnyLanguagesSelected = !!(languagesToTranslateSetting && languagesToTranslateSetting.Values.length > 0)

    const handleAvatarModalClose = () => setShow(false)
    const handleAvatarModalOpen = () => setShow(true)
    const uploadAvatar = async (avatar?: FileUpload<Blob>) => {
        await dispatch(updateAvatar(avatar))
        setShow(false)
    }

    return (
        <Form
            className="user-profile-form"
            onSubmit={handleSubmit}
            data-testid={testId.userProfileForm}
            onKeyPress={preventSubmitOnEnter}
        >
            <Container fluid className="p-0">
                <Row>
                    <Col className="section" md={2}>
                        <div className="section__header">{t(`${tNamespace}avatar`)}</div>
                        <div className="section__body">
                            <div className="avatar-input">
                                <div className="avatar-input__img">
                                    <UserAvatar src={user.Picture || defaultAvatar} />
                                </div>
                                <div className="avatar-input__btn">
                                    <Button className="btn-link" variant="link" onClick={handleAvatarModalOpen}>
                                        <FontAwesomeIcon className="btn-link__icon" icon={faCamera} />
                                        {t(formTranslation.change)}
                                    </Button>
                                </div>
                            </div>
                            <ImageUploadModal
                                circularCrop={true}
                                show={show}
                                onClose={handleAvatarModalClose}
                                onUpload={uploadAvatar}
                                uploading={updateAvatarState.inProcess}
                                pictureSrc={user.Picture}
                            />
                        </div>
                    </Col>
                    <Col className="section" md={5}>
                        <div className="section__header">{t(`${tNamespace}personal-info`)}</div>
                        <div className="section__body">
                            <HorizontalValidatableInput
                                id="formFirstName"
                                type="text"
                                name={nameof<ProfileValues>("firstName")}
                                label={t(formTranslation.firstName)}
                                labelCol={{ sm: 5 }}
                                inputCol={{ sm: 7 }}
                            />
                            <HorizontalValidatableInput
                                id="formLastName"
                                type="text"
                                name={nameof<ProfileValues>("lastName")}
                                label={t(formTranslation.lastName)}
                                labelCol={{ sm: 5 }}
                                inputCol={{ sm: 7 }}
                            />
                        </div>
                    </Col>
                    <Col className="section" md={5}>
                        <div className="section__header">{t(`${tNamespace}system-info`)}</div>
                        <div className="section__body">
                            <HorizontalValidatableInput
                                id="formUiLang"
                                as="select"
                                name={nameof<ProfileValues>("language")}
                                label={t(formTranslation.uiLanguage)}
                                labelCol={{ sm: 5 }}
                                inputCol={{ sm: 7 }}
                            >
                                {locales.map(l => (
                                    <option key={l.id} value={l.id}>
                                        {l.name}
                                    </option>
                                ))}
                            </HorizontalValidatableInput>
                            {isAnyLanguagesSelected && (
                                <HorizontalValidatableInput
                                    id="formTranslateLang"
                                    as="select"
                                    name={nameof<ProfileValues>("translateLanguage")}
                                    label={t(formTranslation.translateLanguage)}
                                    labelCol={{ sm: 5 }}
                                    inputCol={{ sm: 7 }}
                                >
                                    <option value="">{t(formTranslation.nothingSelected)}</option>
                                    {languagesToTranslate.map(language => (
                                        <option key={language} value={language}>
                                            {language}
                                        </option>
                                    ))}
                                </HorizontalValidatableInput>
                            )}
                            <HorizontalValidatableInput
                                id="formMaxCapacity"
                                readOnly
                                type="number"
                                name={nameof<ProfileValues>("maxCapacity")}
                                label={t(formTranslation.maxConcurrentTasks)}
                                labelCol={{ sm: 5 }}
                                inputCol={{ sm: 7 }}
                            />
                        </div>
                    </Col>
                </Row>
                <Row>
                    <Col className="section" md={{ span: 5, offset: 2 }}>
                        <div className="section__header">{t(`${tNamespace}contact-info`)}</div>
                        <div className="section__body">
                            <PhoneInput name={nameof<ProfileValues>("phoneNumber")} />
                            <HorizontalValidatableInput
                                id="formEmail"
                                type="text"
                                name={nameof<ProfileValues>("email")}
                                readOnly
                                label={t(formTranslation.email)}
                                labelCol={{ sm: 5 }}
                                inputCol={{ sm: 7 }}
                            />
                        </div>
                    </Col>
                    <Col className="section" md={5}>
                        <div className="section__header">{t(`${tNamespace}security`)}</div>
                        <div className="section__body">
                            <PasswordInput name={nameof<ProfileValues>("password")} />
                            <AuthenticationTokenInput />
                        </div>
                    </Col>
                </Row>
                <Row>
                    {isMessageSendBehaviourSettingEnabled && (
                        <Col className="section" md={{ span: 5, offset: 2 }}>
                            <div className="section__header">{t(`${tNamespace}workplace-personalization`)}</div>
                            <div className="section__body">
                                <div className="section__body-text">{t(`${tNamespace}send-settings`)}</div>
                                <RadioButtonValidatableInput
                                    value={SendKeyCombinations.Enter}
                                    id="formFirstKeyCombination"
                                    name={nameof<ProfileValues>("keyCombination")}
                                    label={renderKeyCombinationLabel(
                                        SendKeyCombinations.Enter,
                                        SendKeyCombinations.ShiftEnter,
                                        t(`${tNamespace}send-settings-options.sending-message`),
                                        t(`${tNamespace}send-settings-options.new-line`)
                                    )}
                                />
                                <RadioButtonValidatableInput
                                    value={SendKeyCombinations.CtrlEnter}
                                    id="formSecondKeyCombination"
                                    name={nameof<ProfileValues>("keyCombination")}
                                    label={renderKeyCombinationLabel(
                                        SendKeyCombinations.CtrlEnter,
                                        SendKeyCombinations.Enter,
                                        t(`${tNamespace}send-settings-options.sending-message`),
                                        t(`${tNamespace}send-settings-options.new-line`)
                                    )}
                                />
                            </div>
                        </Col>
                    )}
                </Row>
                <Row className="user-profile-form__submit justify-content-md-center">
                    <Col md="auto">
                        {isFormDirty && (
                            <LoadingButton
                                type="submit"
                                className="btn-submit"
                                loading={updateUserState.inProcess}
                                variant="primary"
                            >
                                {t(formTranslation.save)}
                            </LoadingButton>
                        )}
                    </Col>
                </Row>
            </Container>
        </Form>
    )
}

const defaultValues = (user: User): ProfileValues => {
    return {
        firstName: user.FirstName,
        lastName: user.LastName,
        phoneNumber: user.Phone,
        email: user.Email || user.Login,
        language: user.Locale || "ru",
        translateLanguage: user.TranslateLocale || "en",
        maxCapacity: user.MaxDialogs,
        keyCombination: user.KeyCombination || SendKeyCombinations.CtrlEnter
    }
}

const createRequest = (values: ProfileValues): UpdateUserRequest => {
    const request: UpdateUserRequest = {
        FirstName: values.firstName,
        LastName: values.lastName,
        Phone: values.phoneNumber,
        Locale: values.language,
        KeyCombination: values.keyCombination,
        TranslateLocale: values.translateLanguage
    }

    if (values.password) {
        request.OldPassword = values.password.currentPassword
        request.NewPassword = values.password.newPassword
    }

    return request
}

const UserProfileForm: React.FC<Props> = props => {
    const { user } = props
    const dispatch = useAppDispatch()

    const validationSchema = getValidationSchema()

    return (
        <Formik
            enableReinitialize
            initialValues={defaultValues(user)}
            validationSchema={validationSchema}
            onSubmit={async (values: ProfileValues, { resetForm }) => {
                const reset = () => resetForm()
                await dispatch(updateUser(createRequest(values), reset))
            }}
        >
            {formikProps => <FormikUserProfileForm {...props} {...formikProps} />}
        </Formik>
    )
}

export default UserProfileForm
